import React, { useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { CFR_VIOLATIONS, getBasicCategories, type CfrViolation } from '../../data/cfr-violations';
import type { HypotheticalViolation } from '../../types/predictions';

interface ViolationSelectorProps {
  onAdd: (violation: HypotheticalViolation) => void;
}

/**
 * ViolationSelector Component
 *
 * Allows users to select and add hypothetical violations for what-if analysis
 *
 * Features:
 * - Searchable dropdown with 50+ CFR violations
 * - Grouped by BASIC category
 * - Shows severity weight in label
 * - Date picker for violation date
 * - OOS (Out of Service) checkbox
 */
export const ViolationSelector: React.FC<ViolationSelectorProps> = ({ onAdd }) => {
  const [selectedCode, setSelectedCode] = useState('');
  const [violationDate, setViolationDate] = useState(new Date().toISOString().split('T')[0]);
  const [oos, setOos] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Get the selected violation details
  const selectedViolation = CFR_VIOLATIONS.find(v => v.code === selectedCode);

  // Filter violations based on search term
  const filteredViolations = searchTerm
    ? CFR_VIOLATIONS.filter(
        v =>
          v.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
          v.description.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : CFR_VIOLATIONS;

  // Group filtered violations by BASIC category
  const groupedViolations = getBasicCategories().reduce((acc, basicId) => {
    const violations = filteredViolations.filter(v => v.basicCategory === basicId);
    if (violations.length > 0) {
      acc[basicId] = violations;
    }
    return acc;
  }, {} as Record<string, CfrViolation[]>);

  const handleAdd = () => {
    if (!selectedCode || !selectedViolation) return;

    const violation: HypotheticalViolation = {
      cfrCode: selectedViolation.code,
      violationDate,
      oos,
      basicId: selectedViolation.basicCategory,
      severity: selectedViolation.severityWeight,
    };

    onAdd(violation);

    // Reset form
    setSelectedCode('');
    setSearchTerm('');
    setOos(false);
    // Keep the date for convenience
  };

  // Get today's date for max date validation
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center gap-2 mb-4">
        <Plus className="w-5 h-5 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-900">Add Hypothetical Violation</h3>
      </div>

      <div className="space-y-4">
        {/* Search Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Search Violations
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by CFR code or description..."
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* CFR Code Dropdown */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            CFR Violation *
          </label>
          <select
            value={selectedCode}
            onChange={(e) => setSelectedCode(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select a violation...</option>

            {Object.entries(groupedViolations).map(([basicId, violations]) => (
              <optgroup key={basicId} label={basicId}>
                {violations.map((v) => (
                  <option key={v.code} value={v.code}>
                    {v.code} - {v.description} (Weight: {v.severityWeight})
                  </option>
                ))}
              </optgroup>
            ))}
          </select>

          {selectedViolation && (
            <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-900">
                <span className="font-semibold">BASIC Category:</span> {selectedViolation.basicCategory}
              </p>
              <p className="text-sm text-blue-900">
                <span className="font-semibold">Severity Weight:</span> {selectedViolation.severityWeight}
                {oos && (
                  <span className="ml-2 text-orange-700 font-semibold">
                    (x3 with OOS = {(selectedViolation.severityWeight * 3).toFixed(1)})
                  </span>
                )}
              </p>
            </div>
          )}
        </div>

        {/* Date Picker */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Violation Date *
          </label>
          <input
            type="date"
            value={violationDate}
            onChange={(e) => setViolationDate(e.target.value)}
            max={today}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            Cannot be in the future. Defaults to today.
          </p>
        </div>

        {/* OOS Checkbox */}
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-start">
            <input
              type="checkbox"
              checked={oos}
              onChange={(e) => setOos(e.target.checked)}
              className="mt-0.5 h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
              id="oos-checkbox"
            />
            <label htmlFor="oos-checkbox" className="ml-3 flex-1">
              <span className="block text-sm font-semibold text-gray-900">
                Out of Service (OOS)
              </span>
              <span className="block text-xs text-gray-600 mt-1">
                Checking this box multiplies the severity weight by 3, significantly increasing the
                BASIC score impact. OOS violations indicate immediate safety hazards.
              </span>
            </label>
          </div>
        </div>

        {/* Add Button */}
        <button
          onClick={handleAdd}
          disabled={!selectedCode || !violationDate}
          className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700
                   disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors
                   font-semibold flex items-center justify-center gap-2 shadow-md
                   hover:shadow-lg disabled:shadow-none"
        >
          <Plus className="w-5 h-5" />
          Add Violation to Scenario
        </button>

        {/* Help Text */}
        <div className="text-xs text-gray-500 text-center">
          Add one or more violations to see their predicted impact on BASIC scores
        </div>
      </div>
    </div>
  );
};

export default ViolationSelector;
